import {
  SBSelectRaw,
  SBState,
  baseReducers,
  getIdOrModelId,
} from '../utils/helpers/ReducerHelper'
import {
  SBAPICreateDispatch,
  SBAPIDeleteDispatch,
  SBAPIFetchDispatch,
  SBAPIFetchPaginatedDispatch,
  SBAPIUpdateDispatch,
} from '../utils/helpers/SBAPIHelper'
import { POI_URL } from '../utils/urls'
import { POI } from '../models/POI'
import { AppDispatch, RootState } from './store'
import { POISchema } from '../models/schema'
import { initialQuery } from '../utils/helpers/CRUDHelper'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { TableParams } from '../models/TableParams'

const initialState: SBState<POI> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'POI',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer

export const {
  getItemsSuccess: getPOIsSuccess,
  setQuery: setPOIQuery,
  resetQueryAndIds: resetPOIQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<{ [key: string]: POI }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectPOIs = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectAllPOIs = () =>
  createSelector([selectRawItems], (items) =>
    Object.entries(items).map((i) => i[1])
  )
export const selectSelectedPOI = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectPOIById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectPOIsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedPOI =
  (POI: POI | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<POI>(POI)))

export const getPOIs = (params: TableParams) =>
  SBAPIFetchPaginatedDispatch<POI>(POI_URL, params, [POISchema], slice.actions)

export const getPOIWithId = (id: number) =>
  SBAPIFetchDispatch<POI>(`${POI_URL}/${id}`, POISchema, slice.actions)

export const createPOI = (POI: POI) =>
  SBAPICreateDispatch<POI>(POI, POI_URL, POISchema, slice.actions)

export const updatePOI = (POI: POI) =>
  SBAPIUpdateDispatch<POI>(POI, `${POI_URL}/${POI.id}`, slice.actions)

export const deletePOI = (POI: POI) =>
  SBAPIDeleteDispatch<POI>(POI, `${POI_URL}/${POI.id}`, slice.actions)

export const updateVisibility = (POI: POI) => {
  return SBAPIUpdateDispatch<POI>(
    POI,
    `${POI_URL}/${POI.id}/visibility`,
    slice.actions
  )
}
